import { goalbookUrls } from '@/util/links'

export interface AuditResult {
  section: string
  audit_title: string
  score: number
  max_score: number
  score_rationale: string
  suggestions: string
}

export interface CriteriaItem {
  label: string
  description: string
  subjects: string[]
  order: number
  released?: boolean
  beta_only?: boolean
}

export interface ReviewCriteriaProfile {
  content_profile: string
  criteria_set_name: string
  criteria_by_type: Record<string, CriteriaItem[]>
}

export async function getContentReviewProfiles(): Promise<ReviewCriteriaProfile[]> {
  const response = await fetch(`${goalbookUrls.pathwaysApi}/content_review/profiles`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  return response.json()
}

function stripMarkdownFences(content: string): string {
  let cleaned = content.trim()
  if (cleaned.startsWith('```json')) cleaned = cleaned.slice(7)
  else if (cleaned.startsWith('```')) cleaned = cleaned.slice(3)
  if (cleaned.endsWith('```')) cleaned = cleaned.slice(0, -3)
  return cleaned.trim()
}

export interface ReviewImpactStatementStreamArgs {
  impactStatementText: string
  contentProfile: string
  subjects: string[]
  betaMode?: boolean
  signal?: AbortSignal
  onProgress?: (partialContent: string) => void
}

export interface ReviewPresentLevelsStreamArgs {
  presentLevelsText: string
  contentProfile: string
  subjects: string[]
  betaMode?: boolean
  signal?: AbortSignal
  onProgress?: (partialContent: string) => void
}

export interface ReviewPresentLevelsWithImpactStreamArgs {
  presentLevelsText: string
  impactStatementText: string
  contentProfile: string
  subjects: string[]
  betaMode?: boolean
  signal?: AbortSignal
  onProgress?: (partialContent: string) => void
}

export async function reviewImpactStatementStream({
  impactStatementText,
  contentProfile,
  subjects,
  betaMode = false,
  signal,
  onProgress,
}: ReviewImpactStatementStreamArgs): Promise<{ audit_results: AuditResult[] }> {
  const response = await fetch(
    `${goalbookUrls.pathwaysApi}/content_review/review_stream`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content_profile: contentProfile,
        grades: [],
        subjects,
        beta_mode: betaMode,
        content: {
          impact: { impact_statement_text: impactStatementText },
        },
      }),
      credentials: 'include',
      signal,
    }
  )

  if (!response.ok || !response.body) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let fullContent = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (!done) {
        buffer += decoder.decode(value, { stream: true })
      } else {
        buffer += decoder.decode()
      }

      const lines = buffer.split('\n')
      buffer = done ? '' : (lines.pop() || '')

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const dataStr = line.slice(6)
        if (!dataStr.trim()) continue

        let data: Record<string, unknown>
        try {
          data = JSON.parse(dataStr)
        } catch (parseError) {
          console.error('[STREAM] Error parsing SSE data:', parseError)
          continue
        }

        if (data.error) throw new Error(data.error as string)
        if (data.done) {
          return JSON.parse(stripMarkdownFences(fullContent))
        }
        if (data.content) {
          fullContent += data.content
          onProgress?.(fullContent)
        }
      }

      if (done) break
    }

    if (fullContent) {
      return JSON.parse(stripMarkdownFences(fullContent))
    }

    throw new Error('Stream ended without completion signal')
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error
    console.error('[STREAM] Fatal error in impact statement review stream:', error)
    throw error
  }
}

export async function reviewPresentLevelsStream({
  presentLevelsText,
  contentProfile,
  subjects,
  betaMode = false,
  signal,
  onProgress,
}: ReviewPresentLevelsStreamArgs): Promise<{ audit_results: AuditResult[] }> {
  const response = await fetch(
    `${goalbookUrls.pathwaysApi}/content_review/review_stream`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content_profile: contentProfile,
        grades: [],
        subjects,
        beta_mode: betaMode,
        content: {
          present_levels: { present_levels_text: presentLevelsText },
        },
      }),
      credentials: 'include',
      signal,
    }
  )

  if (!response.ok || !response.body) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let fullContent = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (!done) {
        buffer += decoder.decode(value, { stream: true })
      } else {
        buffer += decoder.decode()
      }

      const lines = buffer.split('\n')
      buffer = done ? '' : (lines.pop() || '')

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const dataStr = line.slice(6)
        if (!dataStr.trim()) continue

        let data: Record<string, unknown>
        try {
          data = JSON.parse(dataStr)
        } catch (parseError) {
          console.error('[STREAM] Error parsing SSE data:', parseError)
          continue
        }

        if (data.error) throw new Error(data.error as string)
        if (data.done) {
          return JSON.parse(stripMarkdownFences(fullContent))
        }
        if (data.content) {
          fullContent += data.content
          onProgress?.(fullContent)
        }
      }

      if (done) break
    }

    if (fullContent) {
      return JSON.parse(stripMarkdownFences(fullContent))
    }

    throw new Error('Stream ended without completion signal')
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error
    console.error('[STREAM] Fatal error in present levels review stream:', error)
    throw error
  }
}

export async function reviewPresentLevelsWithImpactStream({
  presentLevelsText,
  impactStatementText,
  contentProfile,
  subjects,
  betaMode = false,
  signal,
  onProgress,
}: ReviewPresentLevelsWithImpactStreamArgs): Promise<{ audit_results: AuditResult[] }> {
  // Only include a content type when its text is non-empty — the backend returns 400
  // if a content key is present but the required text field is empty.
  const content: Record<string, unknown> = {}
  if (presentLevelsText.trim()) {
    content.present_levels = { present_levels_text: presentLevelsText }
  }
  if (impactStatementText.trim()) {
    content.impact = { impact_statement_text: impactStatementText }
  }

  const response = await fetch(
    `${goalbookUrls.pathwaysApi}/content_review/review_stream`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content_profile: contentProfile,
        grades: [],
        subjects,
        beta_mode: betaMode,
        content,
      }),
      credentials: 'include',
      signal,
    }
  )

  if (!response.ok || !response.body) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let fullContent = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (!done) {
        buffer += decoder.decode(value, { stream: true })
      } else {
        buffer += decoder.decode()
      }

      const lines = buffer.split('\n')
      buffer = done ? '' : (lines.pop() || '')

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const dataStr = line.slice(6)
        if (!dataStr.trim()) continue

        let data: Record<string, unknown>
        try {
          data = JSON.parse(dataStr)
        } catch (parseError) {
          console.error('[STREAM] Error parsing SSE data:', parseError)
          continue
        }

        if (data.error) throw new Error(data.error as string)
        if (data.done) {
          return JSON.parse(stripMarkdownFences(fullContent))
        }
        if (data.content) {
          fullContent += data.content
          onProgress?.(fullContent)
        }
      }

      if (done) break
    }

    if (fullContent) {
      return JSON.parse(stripMarkdownFences(fullContent))
    }

    throw new Error('Stream ended without completion signal')
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error
    console.error('[STREAM] Fatal error in present levels + impact review stream:', error)
    throw error
  }
}
